/**
 * EMZI NEXUS V6.3 - Content Studio V2 API Client
 * Complete rebuild with 9-step wizard, conditional branching, 10 Knowledge Packs
 */
import { BACKEND_URL } from '../utils/backendUrl';

// ============================================================
// SOA DRILL SETTINGS (admin-configurable thresholds)
// ============================================================
export const getSOASettings = async (token) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/soa/settings`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('Failed to load SOA settings');
  return response.json();
};

// ============================================================
// STEP 1: OBJECTIVE
// ============================================================

export const setObjective = async (token, sessionId, userId, objectiveType) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/objective`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      user_id: userId,
      objective_type: objectiveType
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to set objective');
  }
  
  return response.json();
};

// ============================================================
// STEP 2: TARGETED AUDIENCE
// ============================================================

export const setTargetedAudience = async (token, sessionId, audienceData) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/targeted-audience`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      ...audienceData
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to set targeted audience');
  }
  
  return response.json();
};

// ============================================================
// STEP 3: KNOWLEDGE SELECTION
// ============================================================

export const selectKnowledge = async (token, sessionId, objectiveType, selections) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/knowledge-selection`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      objective_type: objectiveType,
      selected_products: selections.products || [],
      selected_services: selections.services || [],
      selected_values: selections.values || []
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to select knowledge');
  }
  
  return response.json();
};

// ============================================================
// STEP 4: SPIDER WEB (KNOWLEDGE WEIGHTS)
// ============================================================

export const setSpiderWebWeights = async (token, sessionId, weights) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/spider-web-weights`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      weights: weights
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to set knowledge weights');
  }
  
  return response.json();
};

// ============================================================
// STEP 5: EVIDENCE MODE
// ============================================================

export const setEvidenceMode = async (token, sessionId, evidenceMode) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/evidence-mode`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      evidence_mode: evidenceMode
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to set evidence mode');
  }
  
  return response.json();
};

// ============================================================
// STEP 6: SOA PRIME (Review & Prime)
// ============================================================

export const primeSOA = async (token, sessionId, benefitEmphasis = null) => {
  const requestBody = {
    session_id: sessionId
  };
  
  // NEW: Include benefit emphasis if provided
  if (benefitEmphasis && benefitEmphasis.length > 0) {
    requestBody.benefit_emphasis = benefitEmphasis;
  }
  
  const response = await fetch(`${BACKEND_URL}/api/v2/content/soa/prime`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(requestBody)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to prime SOA questions');
  }
  
  return response.json();
};

// ============================================================
// STEP 7: SOA DRILL
// ============================================================

export const startSOADrill = async (token, sessionId, objectiveType, knowledgeContext) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/soa/start`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      objective_type: objectiveType,
      knowledge_context: knowledgeContext
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to start SOA Drill');
  }
  
  return response.json();
};

export const answerSOAQuestion = async (token, sessionId, soaSessionId, questionId, answer, loopNumber = 1) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/soa/answer`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      soa_session_id: soaSessionId,
      question_id: questionId,
      answer: answer,
      loop_number: loopNumber
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to answer SOA question');
  }
  
  return response.json();
};

export const finalizeSOADrill = async (token, sessionId, soaSessionId, proceedAnyway = false) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/soa/finalize`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      soa_session_id: soaSessionId,
      proceed_anyway: proceedAnyway
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to finalize SOA Drill');
  }
  
  return response.json();
};

// ============================================================
// SOA DRILL V2 - Adaptive AI-Coached Evaluation
// ============================================================

export const evaluateAnswerV2 = async (token, sessionId, questionIndex, answer, attemptNo) => {
  const doCall = (authToken) => fetch(`${BACKEND_URL}/api/v2/content/soa/answer/v2`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      question_index: questionIndex,
      answer: answer,
      attempt_no: attemptNo
    })
  });

  let response = await doCall(token);

  // If unauthorized, try one silent refresh + retry before bubbling the error.
  // Long-running SOA drills (15-20 mins per question with multiple LLM calls)
  // are the most exposed to mobile tab-kills and stale tokens, so we add a
  // best-effort safety net here.
  if (response.status === 401 || response.status === 403) {
    try {
      const refreshToken = localStorage.getItem('nexus_refresh_token');
      if (refreshToken) {
        const r = await fetch(
          `${BACKEND_URL}/api/auth/refresh?refresh_token=${encodeURIComponent(refreshToken)}`,
          { method: 'POST' }
        );
        if (r.ok) {
          const data = await r.json();
          if (data.access_token) {
            localStorage.setItem('nexus_access_token', data.access_token);
            response = await doCall(data.access_token);
          }
        }
      }
    } catch (_e) {
      // fall through — let the original error surface below
    }
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to evaluate answer');
  }

  return response.json();
};

export const acceptAIRescue = async (token, sessionId, questionIndex, aiRescueAnswer = null) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/soa/accept-rescue/v2`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      question_index: questionIndex,
      ai_rescue_answer: aiRescueAnswer
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to accept AI Rescue');
  }
  
  return response.json();
};

export const finalizeSOADrillV2 = async (token, sessionId, proceedAnyway = false) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/soa/finalize/v2`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      proceed_anyway: proceedAnyway
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to finalize SOA Drill');
  }
  
  return response.json();
};

// ============================================================
// STEP 6: SPLIT-TEST SCRIPTS
// ============================================================

export const generateScripts = async (token, sessionId, soaSessionId) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/script/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      soa_session_id: soaSessionId
    })
  });
  
  if (!response.ok) {
    // Enhanced error handling with comprehensive logging
    console.error('[API] Script generation failed:', {
      status: response.status,
      statusText: response.statusText,
      url: response.url
    });
    
    try {
      const error = await response.json();
      console.error('[API] Error response body:', error);
      
      // Handle structured error format: {detail: {code, message, meta}}
      if (error.detail && typeof error.detail === 'object') {
        const err = new Error(error.detail.message || 'Failed to generate scripts');
        err.detail = error.detail; // Preserve structured error
        err.status = response.status;
        throw err;
      }
      
      // Legacy format
      const err = new Error(error.detail || error.message || 'Failed to generate scripts');
      err.detail = error.detail || error;
      err.status = response.status;
      throw err;
      
    } catch (jsonError) {
      // If JSON parsing fails, try to get response as text
      console.error('[API] Failed to parse JSON error:', jsonError);
      try {
        const text = await response.text();
        console.error('[API] Raw error response:', text);
        const err = new Error(`Script generation failed: ${response.status} ${response.statusText}`);
        err.detail = { code: 'PARSE_ERROR', message: text || response.statusText, status: response.status };
        throw err;
      } catch {
        throw new Error(`Script generation failed: ${response.status} ${response.statusText}`);
      }
    }
  }
  
  return response.json();
};

export const selectScript = async (token, sessionId, selectedVariant, userNotes = null) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/script/select`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      selected_variant: selectedVariant,
      user_notes: userNotes
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to select script');
  }
  
  return response.json();
};

// ============================================================
// STEP 7: PLATFORM & DURATION
// ============================================================

export const setPlatformDuration = async (token, sessionId, platforms, duration) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/platform-duration`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      platforms: platforms,
      duration: duration
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to set platform and duration');
  }
  
  return response.json();
};

// ============================================================
// STEP 8: FORMAT SUGGESTION
// ============================================================

export const suggestFormats = async (token, sessionId) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/format/suggest`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to suggest formats');
  }
  
  return response.json();
};

export const selectFormat = async (token, sessionId, selectedFormatId) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/format/select`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      selected_format_id: selectedFormatId
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to select format');
  }
  
  return response.json();
};

// ============================================================
// STEP 9: REVIEW & SUBMIT
// ============================================================

export const submitForApproval = async (token, sessionId, projectName = null, finalNotes = null) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/submit-for-approval`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      project_name: projectName,
      final_notes: finalNotes
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to submit for approval');
  }
  
  return response.json();
};

export const supervisorDecide = async (token, sessionId, submissionId, decision, comment = null, requestedChanges = null) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/approval/decide`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      submission_id: submissionId,
      decision: decision,
      supervisor_comment: comment,
      requested_changes: requestedChanges
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to process supervisor decision');
  }
  
  return response.json();
};

// ============================================================
// PRODUCTION BLUEPRINT
// ============================================================

export const generateProductionBlueprint = async (token, sessionId, submissionId) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/production/blueprint`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      submission_id: submissionId
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to generate production blueprint');
  }
  
  return response.json();
};

// ============================================================
// UTILITY FUNCTIONS
// ============================================================

export const getSession = async (token, sessionId) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/session/${sessionId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get session');
  }
  
  return response.json();
};

export const getKnowledgePack = async (token, type, id) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/knowledge/${type}/${id}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get knowledge pack');
  }
  
  return response.json();
};

// Get Knowledge Packs by Type (single type)
export const getKnowledgePacksByType = async (token, packType) => {
  const response = await fetch(`${BACKEND_URL}/api/v3/packs?pack_type=${packType}&status=published`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to fetch ${packType} packs`);
  }
  
  return response.json();
};

// Get Knowledge Packs by Multiple Types (comma-separated)
export const getKnowledgePacksByTypes = async (token, packTypes) => {
  // For product packs, don't filter by status (include draft for V4 development)
  // For other pack types, only show published
  const isDevelopment = packTypes.includes('product');
  const statusParam = isDevelopment ? '' : '&status=published';
  
  const response = await fetch(`${BACKEND_URL}/api/v3/packs?pack_type=${packTypes}${statusParam}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to fetch packs`);
  }
  
  return response.json();
};

// ============================================================
// CREATIVE INTENT (Step 6 Enhancement)
// ============================================================

export const saveCreativeIntent = async (token, sessionId, creativeIntent) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/session/creative-intent`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      ...creativeIntent
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to save creative intent');
  }
  
  return response.json();
};

// ============================================================
// FORMAT INTELLIGENCE PACK
// ============================================================

export const getFormatIntelligencePack = async (token) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/format-intelligence`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to load Format Intelligence Pack');
  }
  
  return response.json();
};

// ============================================================
// STORYBOARD (Step 10)
// ============================================================

export const generateStoryboard = async (token, sessionId) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/storyboard/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to generate storyboard');
  }
  
  return response.json();
};

export const submitStoryboardForApproval = async (token, sessionId, projectName = null, notes = null) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/storyboard/approve`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      project_name: projectName,
      final_notes: notes
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to submit for approval');
  }
  
  return response.json();
};


// ============================================================
// SESSION DATA - Get full session for review
// ============================================================

export const getSessionData = async (token, sessionId) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/session/${sessionId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to get session data");
  }
  
  return response.json();
};

// ============================================================
// MY SUBMISSIONS - Get executor submissions
// ============================================================

export const getMySubmissions = async (token, userId) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/submissions/mine?user_id=${userId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to get submissions");
  }
  
  return response.json();
};


// ============================================================
// VIDEO SUBMISSION - Submit final video after production
// ============================================================

export const submitVideo = async (token, sessionId, videoUrl, notes = '') => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/video/submit`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      video_url: videoUrl,
      notes: notes
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to submit video");
  }
  
  return response.json();
};

// ============================================================
// VIDEO APPROVE - Supervisor approve/reject video
// ============================================================

export const approveVideo = async (token, sessionId, action, comment = '') => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/video/approve`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      action: action,  // 'approve' or 'reject'
      comment: comment
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to process video");
  }
  
  return response.json();
};

// ============================================================
// VIDEO QUEUE - Get pending videos for supervisor
// ============================================================

export const getVideoQueue = async (token, teamId = 'Content') => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/video/queue?team_id=${encodeURIComponent(teamId)}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to get video queue");
  }
  
  return response.json();
};

// ============================================================
// SUPERVISOR STATS - Get dashboard statistics
// ============================================================

export const getSupervisorStats = async (token, teamId = 'Content') => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/supervisor/stats?team_id=${encodeURIComponent(teamId)}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to get supervisor stats");
  }
  
  return response.json();
};

// ============================================================
// SUBMISSIONS QUEUE - Get all submissions for supervisor review
// ============================================================

export const getSubmissionsQueue = async (token, teamId = 'Content') => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/submissions/queue?team_id=${encodeURIComponent(teamId)}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to get submissions queue");
  }
  
  return response.json();
};

// ============================================================
// UPDATE SCRIPT VARIANT - Save manually edited script
// ============================================================

export const updateScriptVariant = async (token, sessionId, variantId, sections) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/script/update`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId,
      variant_id: variantId,
      sections: sections
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to update script");
  }
  
  return response.json();
};

// ============================================================
// RESUBMIT FOR APPROVAL - Change status back to pending_approval
// ============================================================

export const resubmitForApproval = async (token, sessionId) => {
  const response = await fetch(`${BACKEND_URL}/api/v2/content/submission/resubmit`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify({
      session_id: sessionId
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to resubmit");
  }
  
  return response.json();
};
